import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import io, { Socket } from 'socket.io-client';

export const SocketContext = createContext<Socket | null>(null);

// Singleton socket instance to prevent multiple connections
let socketInstance: Socket | null = null;

// Global notification listeners
const globalListeners = new Set<(event: string, data: any) => void>();

export const addGlobalListener = (callback: (event: string, data: any) => void) => {
  globalListeners.add(callback);
};

export const removeGlobalListener = (callback: (event: string, data: any) => void) => {
  globalListeners.delete(callback);
};

// Deprecated: keep for backward compatibility until refactor is complete
export const setGlobalNotificationCallback = (callback: ((event: string, data: any) => void) | null) => {
  if (callback) {
    globalListeners.add(callback);
  } else {
    // If null is passed, we might want to clear all? Or just do nothing as we are moving away from single callback
    // For safety in legacy code that might try to clear it, let's not clear everything to avoid breaking other listeners
    // But since legacy code expects exclusive control, maybe we should?
    // Let's just log a warning or ignore clearing for now to protect other listeners
    console.warn('setGlobalNotificationCallback is deprecated. Use addGlobalListener/removeGlobalListener instead.');
  }
};

export const useSocket = () => {
  const socket = useContext(SocketContext);
  if (!socket) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return socket;
};

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple socket connections in React strict mode
    if (socketInstance && !socketInstance.disconnected) {
      setSocket(socketInstance);
      return;
    }

    const socketUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    console.log('🔌 Socket connecting to:', socketUrl);

    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    socketInstance = newSocket;

    newSocket.on('connect', () => {
      console.log('✅ Socket connected:', newSocket.id);
    });

    newSocket.on('disconnect', (reason) => {
      console.warn('⚠️ Socket disconnected:', reason);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket connection error:', error.message);
    });

    // Global listeners for reservation events
    const notifyListeners = (event: string, data: any) => {
      globalListeners.forEach(listener => listener(event, data));
    };

    newSocket.on('reservationCreated', (data) => {
      notifyListeners('reservationCreated', data);
    });

    newSocket.on('reservationUpdated', (data) => {
      notifyListeners('reservationUpdated', data);
    });

    newSocket.on('reservationDeleted', (data) => {
      notifyListeners('reservationDeleted', data);
    });

    newSocket.on('reservationRestored', (data) => {
      notifyListeners('reservationRestored', data);
    });

    setSocket(newSocket);
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      // Socket cleanup handled by singleton pattern
    };
  }, []);

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
};